import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, TrendingUp, Target, AlertCircle, Calendar, Flame } from "lucide-react";
import { aiAPI, progressAPI } from "../../api";
import { getStats } from "../../gamification";
import Chip from "../../components/atoms/Chip";
import Card from "../../components/atoms/Card";
import Button from "../../components/atoms/Button";

// Computes the user's snapshot for Penny to coach against.
function computeSnapshot() {
  const stats = getStats();
  const currentDay = Number(localStorage.getItem("currentDay") || 1);
  const testScores = (() => {
    try { return JSON.parse(localStorage.getItem("testScores") || "[]"); } catch { return []; }
  })();
  const recent = testScores.slice(-5);
  const avgRecent = recent.length
    ? Math.round(recent.reduce((a, t) => a + (t.score / t.total) * 100, 0) / recent.length)
    : null;
  return {
    streak: stats.streak || 0,
    totalXp: stats.totalXp || 0,
    lessonsCompleted: stats.lessonsCompleted || 0,
    speakingSessions: stats.speakingSessions || 0,
    currentDay,
    avgRecentScore: avgRecent,
    recentTests: recent.length,
  };
}

// Heuristic insights computed client-side. Penny then names the next thing
// based on which area is weakest.
function deriveInsights(s) {
  const out = [];
  if (s.streak === 0)            out.push({ kind: "alert",   text: "No active streak. Day 1 of a new one is now." });
  else if (s.streak >= 7)        out.push({ kind: "good",    text: `${s.streak} days in a row — top 10% of learners.` });
  else                           out.push({ kind: "info",    text: `${s.streak}-day streak. Today keeps it.` });

  if (s.speakingSessions === 0)  out.push({ kind: "alert",   text: "You've done zero speaking sessions. The biggest score gap is here." });
  else if (s.speakingSessions < s.lessonsCompleted / 2) out.push({ kind: "info", text: "Written progress is ahead of oral. Tip the balance this week." });
  else                           out.push({ kind: "good",    text: "Speaking and writing are in balance — keep both moving." });

  if (s.avgRecentScore !== null) {
    if (s.avgRecentScore >= 85)  out.push({ kind: "good",    text: `Recent tests averaging ${s.avgRecentScore}% — ready for the next level.` });
    else if (s.avgRecentScore < 60) out.push({ kind: "alert", text: `Recent tests averaging ${s.avgRecentScore}%. Slow down — review the last three lessons before moving forward.` });
    else                         out.push({ kind: "info",    text: `Recent average ${s.avgRecentScore}%. Steady — push for 80% on the next test.` });
  }

  return out;
}

export default function Insights() {
  const [snapshot, setSnapshot] = useState(null);
  const [insight, setInsight]   = useState(null);
  const [loadingInsight, setLoadingInsight] = useState(false);

  useEffect(() => {
    // Sync currentDay from server first
    progressAPI.get().then((data) => {
      if (data.currentDay) localStorage.setItem("currentDay", String(data.currentDay));
      setSnapshot(computeSnapshot());
    }).catch(() => setSnapshot(computeSnapshot()));
  }, []);

  async function fetchPennyInsight() {
    if (!snapshot) return;
    setLoadingInsight(true);
    try {
      const data = await aiAPI.pennyInsight(snapshot);
      setInsight(data.insight);
    } catch {
      setInsight("Open today's lesson — that's the only thing that moves the needle.");
    } finally {
      setLoadingInsight(false);
    }
  }

  if (!snapshot) return <div style={{ padding: 48, textAlign: "center", color: "var(--fg-3)" }}>Loading…</div>;

  const insights = deriveInsights(snapshot);

  return (
    <div className="page-enter" style={{ paddingBottom: 64, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Link to="/penny" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--fg-3)", fontSize: 13, fontWeight: 500, textDecoration: "none", marginBottom: 12 }}>
          <ArrowLeft size={14} strokeWidth={1.6} /> Penny
        </Link>
        <Chip tone="gold">Penny Insights</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          What Penny <em style={{ color: "var(--gold-500)" }}>noticed this week.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.55, margin: 0, maxWidth: "62ch" }}>
          Three things Penny picked up from how you've been showing up — and one specific next step.
        </p>
      </div>

      {/* AI coaching prompt */}
      <Card paperDeep>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <Sparkles size={18} strokeWidth={1.6} color="var(--gold-500)" />
          <span className="eyebrow">Penny says</span>
        </div>
        {insight ? (
          <p className="font-display" style={{ fontSize: 28, lineHeight: 1.25, margin: "0 0 16px", fontWeight: 400, color: "var(--fg-1)", maxWidth: "30ch" }}>
            "{insight}"
          </p>
        ) : (
          <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.6, margin: "0 0 16px", maxWidth: "60ch" }}>
            Ask Penny what to focus on next, based on your last five lessons and tests.
          </p>
        )}
        <Button variant="primary" onClick={fetchPennyInsight} disabled={loadingInsight} iconLeft={<Sparkles size={14} strokeWidth={1.8} />}>
          {loadingInsight ? "Penny is thinking…" : insight ? "Ask again" : "Ask Penny"}
        </Button>
      </Card>

      {/* Snapshot stats */}
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <StatCard label="Streak"        value={`${snapshot.streak}d`}     spine="var(--rust)" Icon={Flame} />
        <StatCard label="Total XP"      value={snapshot.totalXp}          spine="var(--gold-500)" Icon={Target} />
        <StatCard label="Lessons done"  value={snapshot.lessonsCompleted} spine="var(--sage)" Icon={Calendar} />
        <StatCard label="Speaking"      value={snapshot.speakingSessions} spine="var(--sky)" Icon={TrendingUp} />
      </div>

      {/* Auto-derived insights */}
      <div>
        <p className="eyebrow" style={{ marginBottom: 12 }}>Three observations</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {insights.map((i, k) => <InsightRow key={k} insight={i} />)}
        </div>
      </div>

      {/* Quick suggested actions */}
      <div>
        <p className="eyebrow" style={{ marginBottom: 12 }}>What to do next</p>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
          <Link to={`/lesson/${snapshot.currentDay}`} style={{ textDecoration: "none" }}>
            <ActionCard label={`Continue Day ${snapshot.currentDay}`} desc="Twenty minutes, written half." />
          </Link>
          <Link to={`/oral/${snapshot.currentDay}`} style={{ textDecoration: "none" }}>
            <ActionCard label="Today's oral practice" desc="Half hour with the mic, no script." />
          </Link>
          <Link to="/write/rephrase" style={{ textDecoration: "none" }}>
            <ActionCard label="Polish a real message" desc="Open Rephrase Studio." />
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, spine, Icon }) {
  return (
    <Card paperDeep spineColor={spine} style={{ padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Icon size={14} strokeWidth={1.6} color={spine} />
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.12em" }}>{label}</span>
      </div>
      <p className="font-display" style={{ fontSize: 36, lineHeight: 1, fontWeight: 400, margin: 0, color: "var(--fg-1)" }}>{value}</p>
    </Card>
  );
}

function InsightRow({ insight }) {
  const palette = insight.kind === "good"
    ? { bg: "#eef5ee", border: "#cfe0ce", color: "var(--sage-dark)" }
    : insight.kind === "alert"
    ? { bg: "#fdf0f1", border: "#ddb1b6", color: "var(--berry-dark)" }
    : { bg: "var(--paper-deep)", border: "var(--border-2)", color: "var(--fg-2)" };

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 16px", background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: "var(--radius-md)" }}>
      <AlertCircle size={16} strokeWidth={1.6} color={palette.color} style={{ marginTop: 2, flexShrink: 0 }} />
      <p style={{ fontSize: 14.5, lineHeight: 1.55, color: palette.color, margin: 0, fontWeight: 500 }}>{insight.text}</p>
    </div>
  );
}

function ActionCard({ label, desc }) {
  return (
    <article style={{ padding: "14px 16px", background: "#fff", border: "1px solid var(--border-1)", borderRadius: "var(--radius-md)", transition: "transform 200ms cubic-bezier(.2,.8,.2,1)", cursor: "pointer" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}>
      <p style={{ fontWeight: 700, fontSize: 14, margin: "0 0 4px", color: "var(--fg-1)" }}>{label}</p>
      <p style={{ fontSize: 12, color: "var(--fg-3)", margin: 0 }}>{desc}</p>
    </article>
  );
}
